// src/components/Settings/AccountForm.js - Updated with subscription cancellation
import React, { useState, useEffect } from "react";
import { auth, db } from "../../firebase/config";
import { doc, getDoc, updateDoc, serverTimestamp, collection, addDoc } from "firebase/firestore";
import { updateProfile, signOut } from "firebase/auth";
import { useNavigate } from "react-router-dom";
import { useUserContext } from "../../contexts/UserContext";
import axios from "axios";

const API_URL = process.env.REACT_APP_API_URL || 'https://southamerica-east1-drecenna.cloudfunctions.net/api';

const AccountForm = ({ user, setError, setSuccess }) => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [displayName, setDisplayName] = useState(user?.displayName || "");
  const [isEditing, setIsEditing] = useState(false);
  const [userData, setUserData] = useState(null);
  const [upgradingPlan, setUpgradingPlan] = useState(false);
  
  // Estados para cancelamento de assinatura
  const [showCancelModal, setShowCancelModal] = useState(false);
  const [cancelingSubscription, setCancelingSubscription] = useState(false);
  
  // Estados para gerenciamento de perfis empresariais
  const [showProfileModal, setShowProfileModal] = useState(false);
  const [newProfileName, setNewProfileName] = useState("");
  const [creatingProfile, setCreatingProfile] = useState(false);
  const [editingProfileId, setEditingProfileId] = useState(null);
  const [confirmDeleteProfileId, setConfirmDeleteProfileId] = useState(null);
  
  // Obter dados do contexto do usuário
  const { 
    subscription,
    activeAccountType,
    activeBusinessProfileId,
    businessProfiles,
    createProfile,
    updateBusinessProfile,
    deleteBusinessProfile,
    handleSwitchAccountType,
    migrateToNewStructure,
    refreshSubscriptionData
  } = useUserContext();

  // Carregar dados completos do usuário do Firestore
  useEffect(() => {
    const loadUserData = async () => {
      if (user) {
        // Determinar o displayName correto para edição
        if (activeAccountType === 'business' && activeBusinessProfileId) {
          // Se estamos em um perfil empresarial, usar o nome do perfil
          const activeProfile = businessProfiles.find(p => p.id === activeBusinessProfileId);
          if (activeProfile) {
            setDisplayName(activeProfile.profileName || user.displayName || "");
          } else {
            setDisplayName(user.displayName || "");
          }
        } else {
          // Se estamos em modo pessoal ou sem perfil definido, usar o displayName padrão
          setDisplayName(user.displayName || "");
        }
        
        try {
          // Buscar dados completos do Firestore
          const userDoc = await getDoc(doc(db, "users", user.uid));
          if (userDoc.exists()) {
            setUserData(userDoc.data());
          }
          
          // Verificar se precisamos migrar dados para a nova estrutura
          if (user.uid && userDoc.exists() && !userDoc.data().migrationCompleted) {
            // Executar migração de dados para a nova estrutura
            try {
              await migrateToNewStructure(user.uid);
              console.log("Migração para nova estrutura concluída");
            } catch (migrationError) {
              console.error("Erro na migração:", migrationError);
            }
          }
        } catch (error) {
          console.error("Erro ao carregar dados do usuário:", error);
        }
      }
    };
    
    loadUserData();
  }, [user, activeAccountType, activeBusinessProfileId, businessProfiles, migrateToNewStructure]);

  // Função para logout
  const handleLogout = async () => {
    try {
      await signOut(auth);
      navigate('/login');
    } catch (error) {
      console.error('Erro ao fazer logout:', error);
      setError(`Falha ao sair: ${error.message}`);
    }
  };

  // Função para iniciar upgrade para o plano Business Pro
  const handleUpgradeToPro = async () => {
    if (!user || !user.email) {
      setError('Você precisa estar logado para fazer upgrade.');
      return;
    }

    setUpgradingPlan(true);
    setError("");

    try {
      const response = await axios.post(`${API_URL}/create-checkout-session`, {
        planType: "business_pro",
        email: user.email
      });

      // Redirecionar para o checkout do Stripe
      window.location.href = response.data.url;
    } catch (error) {
      console.error('Erro ao iniciar checkout:', error);
      setError('Erro ao iniciar o processo de upgrade. Por favor, tente novamente.');
    } finally {
      setUpgradingPlan(false);
    }
  };

  // Função para cancelar assinatura
  const handleCancelSubscription = async () => {
    if (!user) {
      setError('Você precisa estar logado para cancelar a assinatura.');
      return;
    }

    setCancelingSubscription(true);
    setError("");
    setSuccess("");

    try {
      // Obter o token de autenticação
      const token = await user.getIdToken();
      
      const response = await axios.post(`${API_URL}/cancel-subscription`, {}, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });

      if (response.data.success) {
        setSuccess(response.data.message);
        
        // Atualizar dados da assinatura
        await refreshSubscriptionData();
        
        // Fechar o modal
        setShowCancelModal(false);
        
        // Limpar mensagem após um tempo maior para que o usuário possa ler
        setTimeout(() => {
          setSuccess("");
        }, 8000);
      } else {
        throw new Error(response.data.message || 'Erro ao cancelar assinatura');
      }
    } catch (error) {
      console.error('Erro ao cancelar assinatura:', error);
      
      if (error.response?.status === 401) {
        setError('Sessão expirada. Por favor, faça login novamente.');
      } else if (error.response?.status === 400) {
        setError(error.response.data.error || 'Não foi possível cancelar a assinatura.');
      } else {
        setError('Erro ao cancelar assinatura. Tente novamente ou entre em contato com o suporte.');
      }
      
      // Limpar erro após um tempo
      setTimeout(() => {
        setError("");
      }, 5000);
    } finally {
      setCancelingSubscription(false);
    }
  };

  // Função para salvar mudanças de perfil - MODIFICADA para salvar no perfil correto
  const handleSaveProfile = async () => {
    if (!displayName.trim()) {
      setError("O nome não pode estar vazio.");
      return;
    }

    try {
      setLoading(true);
      setError("");
      setSuccess("");
      
      const currentUser = auth.currentUser;
      if (!currentUser) {
        throw new Error("Usuário não autenticado.");
      }
      
      // Se estamos em um perfil empresarial específico, atualizar o nome dele
      if (activeAccountType === 'business' && activeBusinessProfileId) {
        // Atualizar o nome do perfil empresarial atual
        const success = await updateBusinessProfile(activeBusinessProfileId, {
          profileName: displayName,
          updatedAt: new Date()
        });
        
        if (success) {
          setSuccess("Nome do perfil atualizado com sucesso!");
        } else {
          throw new Error("Erro ao atualizar o perfil empresarial.");
        }
      } else {
        // Atualizar displayName no Firebase Auth
        await updateProfile(currentUser, {
          displayName: displayName
        });
        
        // Atualizar displayName no Firestore (se esse documento existir)
        const userDocRef = doc(db, "users", currentUser.uid);
        const userDoc = await getDoc(userDocRef);
        
        if (userDoc.exists()) {
          await updateDoc(userDocRef, {
            displayName: displayName,
            updatedAt: serverTimestamp()
          });
        }
        
        setSuccess("Nome atualizado com sucesso!");
      }
      
      setIsEditing(false);
      
      // Limpar mensagem após 3 segundos
      setTimeout(() => {
        setSuccess("");
      }, 3000);
      
    } catch (error) {
      console.error("Erro ao salvar nome:", error);
      setError(`Erro ao atualizar nome: ${error.message}`);
    } finally {
      setLoading(false);
    }
  };

  // MODIFICADO: Função para criar novo perfil empresarial usando o nome como ID
  const handleCreateProfile = async () => {
    if (!newProfileName.trim()) {
      setError("O nome do perfil não pode estar vazio.");
      return;
    }
    setCreatingProfile(true);
    setError("");
    try {
      // Verificar limites com base no plano - AJUSTADO CONFORME SOLICITAÇÃO
      const maxProfiles = subscription?.planType === 'business_pro' ? 2 : 
                         subscription?.planType === 'business' ? 1 : 1;
      
      if (businessProfiles.length >= maxProfiles) {
        throw new Error(`Sua conta não consegue criar mais de ${maxProfiles === 1 ? 'uma empresa' : `${maxProfiles} empresas`}, contate o suporte caso queira adicionar mais empresas.`);
      }
      
      // Criar o perfil usando a função do contexto
      // Esta função agora usa o nome do perfil para criar o ID
      const profileId = await createProfile({ profileName: newProfileName }, 'business');
      
      if (profileId) {
        setSuccess(`Perfil empresarial "${newProfileName}" criado com sucesso!`);
        setNewProfileName("");
        setShowProfileModal(false);
        
        // Alternar para o novo perfil
        handleSwitchAccountType('business', profileId);
      } else {
        throw new Error("Não foi possível criar o perfil empresarial.");
      }
    } catch (error) {
      console.error("Erro ao criar perfil:", error);
      setError(error.message || "Erro ao criar o perfil empresarial.");
    } finally {
      setCreatingProfile(false);
    }
  };
  
  // Função para atualizar nome de perfil
  const handleUpdateProfileName = async () => {
    if (!displayName.trim() || !editingProfileId) {
      setError("O nome do perfil não pode estar vazio.");
      return;
    }
    
    setLoading(true);
    
    try {
      // Atualizar o perfil usando a função do contexto
      const success = await updateBusinessProfile(editingProfileId, {
        profileName: displayName
      });
      
      if (success) {
        setSuccess("Nome do perfil atualizado com sucesso!");
        setEditingProfileId(null);
        setIsEditing(false);
      } else {
        throw new Error("Não foi possível atualizar o perfil.");
      }
    } catch (error) {
      console.error("Erro ao atualizar perfil:", error);
      setError(error.message || "Erro ao atualizar o perfil.");
    } finally {
      setLoading(false);
    }
  };
  
  // Função para excluir perfil empresarial
  const handleDeleteProfile = async () => {
    if (!confirmDeleteProfileId) return;
    
    setLoading(true);
    
    try {
      // Verificar se estamos tentando excluir o único perfil
      if (businessProfiles.length <= 1) {
        throw new Error("Não é possível excluir o único perfil empresarial.");
      }
      
      // Excluir o perfil usando a função do contexto
      const success = await deleteBusinessProfile(confirmDeleteProfileId);
      
      if (success) {
        setSuccess("Perfil empresarial excluído com sucesso!");
        setConfirmDeleteProfileId(null);
      } else {
        throw new Error("Não foi possível excluir o perfil.");
      }
    } catch (error) {
      console.error("Erro ao excluir perfil:", error);
      setError(error.message || "Erro ao excluir o perfil empresarial.");
    } finally {
      setLoading(false);
    }
  };

  // Função para traduzir o tipo de plano para um nome amigável
  const getPlanName = (planType) => {
    const planNames = {
      'personal': 'Pessoal',
      'business': 'Empresarial',
      'business_pro': 'Empresarial Pro'
    };
    
    return planNames[planType] || planType || 'Desconhecido';
  };

  // Função para formatar data
  const formatDate = (timestamp) => {
    if (!timestamp) return "Não disponível";
    
    const date = timestamp.toDate ? timestamp.toDate() : new Date(timestamp);
    
    return new Intl.DateTimeFormat('pt-BR', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
    }).format(date);
  };

  // Verificar se o usuário tem plano Business e pode fazer upgrade para Pro
  const canUpgradeToPro = activeAccountType === 'business' && 
                          userData?.planType === 'business' && 
                          userData?.subscriptionStatus === 'active';
  
  // Verificar se o usuário tem plano Pro (para gerenciar múltiplos perfis)
  const hasPro = userData?.planType === 'business_pro' && 
                userData?.subscriptionStatus === 'active';

  // Verificar se pode cancelar assinatura
  const canCancelSubscription = subscription?.active && 
                               !subscription?.isCanceled && 
                               !subscription?.cancelAtPeriodEnd &&
                               subscription?.status === 'active';

  // Renderizar o modal de confirmação de cancelamento
  const renderCancelConfirmationModal = () => {
    if (!showCancelModal) return null;
    
    const endDate = subscription?.currentPeriodEnd 
      ? new Date(subscription.currentPeriodEnd).toLocaleDateString('pt-BR')
      : 'data não disponível';
    
    return (
      <div className="modal-overlay">
        <div className="modal-container">
          <div className="modal-header">
            <h3>Confirmar Cancelamento</h3>
            <button 
              className="modal-close-button"
              onClick={() => setShowCancelModal(false)}
              disabled={cancelingSubscription}
            >
              &times;
            </button>
          </div>
          <div className="modal-body">
            <p className="warning-text">
              <strong>Atenção:</strong> Você está prestes a cancelar sua assinatura.
            </p>
            <p>
              Após o cancelamento:
            </p>
            <ul style={{ marginLeft: '20px', marginBottom: '15px' }}>
              <li>Você continuará tendo acesso a todos os recursos até <strong>{endDate}</strong></li>
              <li>Sua assinatura não será renovada automaticamente</li>
              <li>Você pode reativar sua assinatura a qualquer momento antes da data de expiração</li>
              <li>Após a data de expiração, o acesso aos recursos premium será suspenso</li>
            </ul>
            <p>
              Tem certeza que deseja cancelar sua assinatura?
            </p>
          </div>
          <div className="modal-footer">
            <button 
              className="cancel-button"
              onClick={() => setShowCancelModal(false)}
              disabled={cancelingSubscription}
            >
              Manter Assinatura
            </button>
            <button 
              className="delete-button"
              onClick={handleCancelSubscription}
              disabled={cancelingSubscription}
            >
              {cancelingSubscription ? (
                <>
                  <span className="button-spinner"></span>
                  <span>Cancelando...</span>
                </>
              ) : "Confirmar Cancelamento"}
            </button>
          </div>
        </div>
      </div>
    );
  };

  // Renderizar o modal de criação/edição de perfil
  const renderProfileModal = () => {
    if (!showProfileModal) return null;
    
    return (
      <div className="modal-overlay">
        <div className="modal-container">
          <div className="modal-header">
            <h3>Novo Perfil Empresarial</h3>
            <button 
              className="modal-close-button"
              onClick={() => {
                setShowProfileModal(false);
                setNewProfileName("");
              }}
            >
              &times;
            </button>
          </div>
          <div className="modal-body">
            <div className="form-group">
              <label htmlFor="profileName">Nome do Perfil:</label>
              <input
                type="text"
                id="profileName"
                value={newProfileName}
                onChange={(e) => setNewProfileName(e.target.value)}
                placeholder="Ex: Minha Segunda Empresa"
                className="form-control"
              />
              <small className="form-text">
                Este nome será utilizado para identificar este perfil empresarial e também como identificador no banco de dados.
              </small>
            </div>
          </div>
          <div className="modal-footer">
            <button 
              className="cancel-button"
              onClick={() => {
                setShowProfileModal(false);
                setNewProfileName("");
              }}
              disabled={creatingProfile}
            >
              Cancelar
            </button>
            <button 
              className="save-button"
              onClick={handleCreateProfile}
              disabled={creatingProfile || !newProfileName.trim()}
            >
              {creatingProfile ? (
                <>
                  <span className="button-spinner"></span>
                  <span>Criando...</span>
                </>
              ) : "Criar Perfil"}
            </button>
          </div>
        </div>
      </div>
    );
  };
  
  // Renderizar o modal de confirmação de exclusão
  const renderDeleteConfirmationModal = () => {
    if (!confirmDeleteProfileId) return null;
    
    // Encontrar o perfil a ser excluído
    const profileToDelete = businessProfiles.find(p => p.id === confirmDeleteProfileId);
    if (!profileToDelete) return null;
    
    return (
      <div className="modal-overlay">
        <div className="modal-container">
          <div className="modal-header">
            <h3>Confirmar Exclusão</h3>
            <button 
              className="modal-close-button"
              onClick={() => setConfirmDeleteProfileId(null)}
            >
              &times;
            </button>
          </div>
          <div className="modal-body">
            <p className="warning-text">
              <strong>Atenção:</strong> Você está prestes a excluir o perfil empresarial "{profileToDelete.profileName}".
            </p>
            <p>
              Esta ação não pode ser desfeita e todos os dados associados a este perfil 
              (categorias, transações, configurações) serão desassociados.
            </p>
            <p>
              Tem certeza que deseja continuar?
            </p>
          </div>
          <div className="modal-footer">
            <button 
              className="cancel-button"
              onClick={() => setConfirmDeleteProfileId(null)}
              disabled={loading}
            >
              Cancelar
            </button>
            <button 
              className="delete-button"
              onClick={handleDeleteProfile}
              disabled={loading}
            >
              {loading ? (
                <>
                  <span className="button-spinner"></span>
                  <span>Excluindo...</span>
                </>
              ) : "Excluir Perfil"}
            </button>
          </div>
        </div>
      </div>
    );
  };

  return (
    <div className="settings-tab-content">
      <h3>Informações da Conta</h3>
      
      <div className="account-info-card">
        {/* Seção de Perfil */}
        <div className="account-info-section">
          <div className="account-info-header">
            <div className="section-icon">
              <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2"></path>
                <circle cx="12" cy="7" r="4"></circle>
              </svg>
            </div>
            <h4>
              {activeAccountType === 'business' 
                ? `Perfil Empresarial${businessProfiles.length > 1 ? ' Ativo' : ''}`
                : 'Perfil Pessoal'}
            </h4>
          </div>
          
          {/* Campo de nome */}
          <div className="info-group">
            <div className="info-label">
              {activeAccountType === 'business' 
                ? 'Nome da Empresa' 
                : 'Nome'}
            </div>
            {isEditing ? (
              <div className="edit-field-container">
                <input
                  type="text"
                  value={displayName}
                  onChange={(e) => setDisplayName(e.target.value)}
                  disabled={loading}
                  placeholder="Nome da empresa"
                  className="edit-field-input"
                />
                <div className="edit-actions">
                  <button 
                    className="edit-action-button cancel"
                    onClick={() => {
                      setIsEditing(false);
                      // Restaurar o nome original
                      if (activeAccountType === 'business' && activeBusinessProfileId) {
                        const activeProfile = businessProfiles.find(p => p.id === activeBusinessProfileId);
                        setDisplayName(activeProfile?.profileName || user?.displayName || "");
                      } else {
                        setDisplayName(user?.displayName || "");
                      }
                    }}
                    disabled={loading}
                    title="Cancelar"
                  >
                    <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                      <line x1="18" y1="6" x2="6" y2="18"></line>
                      <line x1="6" y1="6" x2="18" y2="18"></line>
                    </svg>
                  </button>
                  <button 
                    className="edit-action-button save"
                    onClick={handleSaveProfile}
                    disabled={loading}
                    title="Salvar"
                  >
                    {loading ? (
                      <span className="button-spinner"></span>
                    ) : (
                      <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                        <polyline points="20 6 9 17 4 12"></polyline>
                      </svg>
                    )}
                  </button>
                </div>
              </div>
            ) : (
              <div className="info-value-container">
                <div className="info-value">{displayName || "Não definido"}</div>
                <button 
                  className="info-edit-button"
                  onClick={() => setIsEditing(true)}
                  title={activeAccountType === 'business' 
                    ? "Editar nome da empresa" 
                    : "Editar nome"}
                >
                  <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <path d="M17 3a2.85 2.85 0 1 1 4 4L7.5 20.5 2 22l1.5-5.5L17 3z"></path>
                  </svg>
                </button>
              </div>
            )}
          </div>
          
          {/* Campo de email */}
          <div className="info-group">
            <div className="info-label">Email</div>
            <div className="info-value-container">
              <div className="info-value with-icon">
                <svg className="info-icon" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M4 4h16c1.1 0 2 .9 2 2v12c0 1.1-.9 2-2 2H4c-1.1 0-2-.9-2-2V6c0-1.1.9-2 2-2z"></path>
                  <polyline points="22,6 12,13 2,6"></polyline>
                </svg>
                {user?.email || userData?.email || "Não disponível"}
              </div>
            </div>
          </div>
        </div>

        {/* Seção de Gerenciamento de Perfis Empresariais - NOVA */}
        {activeAccountType === 'business' && businessProfiles.length > 0 && activeAccountType !== 'personal' && (
          <div className="account-info-section">
            <div className="account-info-header">
              <div className="section-icon">
                <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <rect x="2" y="7" width="20" height="14" rx="2" ry="2"></rect>
                  <path d="M16 21V5a2 2 0 0 0-2-2h-4a2 2 0 0 0-2 2v16"></path>
                </svg>
              </div>
              <h4>Perfis Empresariais</h4>
              
              {/* Botão para adicionar perfil */}
              {businessProfiles.length < (hasPro ? 2 : 1) && (
                <button 
                  className="create-profile-button"
                  onClick={() => {
                    setNewProfileName("");
                    setShowProfileModal(true);
                  }}
                  title="Adicionar novo perfil"
                >
                  <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <line x1="12" y1="5" x2="12" y2="19"></line>
                    <line x1="5" y1="12" x2="19" y2="12"></line>
                  </svg>
                  <span>Novo Perfil</span>
                </button>
              )}
            </div>
            
            <p className="profiles-description">
              {hasPro 
                ? `Você pode criar até 2 perfis empresariais. Para adicionar mais empresas, contate o suporte.`
                : `Sua conta permite apenas 1 perfil empresarial. Para adicionar mais empresas, contate o suporte.`}
            </p>
            
            {/* Lista de perfis */}
            <div className="business-profiles-list">
              {businessProfiles.map(profile => (
                <div 
                  key={profile.id} 
                  className={`profile-item ${activeBusinessProfileId === profile.id ? 'active' : ''}`}
                >
                  <div className="profile-icon">
                    <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                      <path d="M3 9l9-7 9 7v11a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2z"></path>
                      <polyline points="9 22 9 12 15 12 15 22"></polyline>
                    </svg>
                  </div>
                  <div className="profile-info">
                    <div className="profile-name">{profile.profileName}</div>
                    <div className="profile-date">Criado em: {formatDate(profile.createdAt)}</div>
                    {profile.isDefault && <div className="profile-badge default-badge">Principal</div>}
                    {/* Adiciona o ID do perfil se for diferente do nome sanitizado */}
                    {profile.id !== 'business' && (
                      <div className="profile-id">ID: {profile.id}</div>
                    )}
                  </div>
                  <div className="profile-actions">
                    {/* Botão para selecionar perfil (se não for o ativo) */}
                    {activeBusinessProfileId !== profile.id && (
                      <button 
                        className="select-profile-button"
                        onClick={() => handleSwitchAccountType('business', profile.id)}
                        title="Selecionar perfil"
                      >
                        <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                          <polyline points="20 6 9 17 4 12"></polyline>
                        </svg>
                      </button>
                    )}
                    
                    {/* Botão para excluir perfil (se tiver mais de um e não for o perfil principal) */}
                    {businessProfiles.length > 1 && !profile.isDefault && (
                      <button 
                        className="delete-profile-button"
                        onClick={() => setConfirmDeleteProfileId(profile.id)}
                        title="Excluir perfil"
                      >
                        <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                          <polyline points="3 6 5 6 21 6"></polyline>
                          <path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2"></path>
                          <line x1="10" y1="11" x2="10" y2="17"></line>
                          <line x1="14" y1="11" x2="14" y2="17"></line>
                        </svg>
                      </button>
                    )}
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Seção de Assinatura */}
        <div className="account-info-section">
          <div className="account-info-header">
            <div className="section-icon">
              <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <path d="M16 4h2a2 2 0 0 1 2 2v14a2 2 0 0 1-2 2H6a2 2 0 0 1-2-2V6a2 2 0 0 1 2-2h2"></path>
                <rect x="8" y="2" width="8" height="4" rx="1" ry="1"></rect>
              </svg>
            </div>
            <h4>Informações de Assinatura</h4>
          </div>
          
          {/* Campo de plano atual com tooltip de validade */}
          <div className="info-group">
            <div className="info-label">
              Plano Atual
            </div>
            <div className="info-value-container">
              <div className={`plan-badge ${userData?.planType || ""}`} data-tooltip={`Válido até: ${formatDate(userData?.currentPeriodEnd)}`}>
                <svg className="plan-icon" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2"></path>
                  <circle cx="12" cy="7" r="4"></circle>
                </svg>
                {getPlanName(userData?.planType)}
                {userData?.subscriptionStatus === 'active' && 
                  <span className="plan-status active">Ativo</span>}
              </div>
            </div>
            
            {/* Botão de upgrade para Pro diretamente abaixo do plano */}
            {canUpgradeToPro && (
              <div className="upgrade-pro-container">
                <button 
                  className="upgrade-pro-button"
                  onClick={handleUpgradeToPro}
                  disabled={upgradingPlan}
                  title="Fazer upgrade para o plano Empresarial Pro"
                  style={{textDecoration: 'none'}}
                >
                  {upgradingPlan ? (
                    <span className="button-spinner"></span>
                  ) : (
                    <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                      <polyline points="23 6 13.5 15.5 8.5 10.5 1 18"></polyline>
                      <polyline points="17 6 23 6 23 12"></polyline>
                    </svg>
                  )}
                  <span style={{textDecoration: 'none'}}>{upgradingPlan ? "Processando..." : "Upgrade para Pro"}</span>
                </button>
              </div>
            )}

            {/* Botão de cancelamento de assinatura */}
            {canCancelSubscription && (
              <div className="cancel-subscription-container">
                <button 
                  className="cancel-subscription-button"
                  onClick={() => setShowCancelModal(true)}
                  disabled={cancelingSubscription}
                >
                  cancelar plano
                </button>
              </div>
            )}

            {/* Mostrar status se assinatura estiver cancelada */}
            {subscription?.isCanceled && subscription?.active && (
              <div className="subscription-status-info">
                <p className="canceled-status-text">
                  ⚠️ Assinatura cancelada - acesso até {formatDate(subscription.currentPeriodEnd)}
                </p>
              </div>
            )}
          </div>
        </div>
        
        {/* Seção de Sessão */}
        <div className="account-info-section">
          <div className="account-info-header">
            <div className="section-icon">
              <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <rect x="3" y="11" width="18" height="11" rx="2" ry="2"></rect>
                <path d="M7 11V7a5 5 0 0 1 10 0v4"></path>
              </svg>
            </div>
            <h4>Sessão</h4>
          </div>
          
          <p className="session-description">
            Encerrar a sessão atual e retornar à tela de login.
          </p>
          
          <button 
            className="logout-button"
            onClick={handleLogout}
          >
            <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <path d="M9 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h4"></path>
              <polyline points="16 17 21 12 16 7"></polyline>
              <line x1="21" y1="12" x2="9" y2="12"></line>
            </svg>
            <span>Sair da Conta</span>
          </button>
        </div>
      </div>
      
      {/* Modais */}
      {renderProfileModal()}
      {renderDeleteConfirmationModal()}
      {renderCancelConfirmationModal()}
    </div>
  );
};

export default AccountForm;